import _ from 'lodash';
import { ConfirmState, RootState } from '@/store/types';
import { ActionTree, MutationTree, Module, GetterTree } from 'vuex';
import { clearErrors, confirmValidationSchema, validateSchema } from '@/utils/validation';
import axios from '@/utils/axios';
import * as NProgress from 'nprogress';

export const state: ConfirmState = {
    refNo: '',
    passwordCreated: false,
    passwordCreationToken: '',
    password: '',
    passwordConfirmation: '',
    passwordStrength: {
        score: 0,
        meetsMinimumLengthRequirement: false,
        containsUpperCaseLetter: false,
        containsLowerCaseLetter: false,
        containsAtLeastOneNumber: false,
        containsSpecialCharacter: false,
        isUncommonPassword: false,
    },
    isSaving: false,
    errors: {},
};

export const getters: GetterTree<ConfirmState, RootState> = {
    getFieldErrors: state => (field: string) => state.errors[field],
    isFormValid: state => _.isEmpty(state.errors),
};

export const mutations: MutationTree<ConfirmState> = {
    init(state, payload) {
        state.refNo = payload.refNo;
        state.passwordCreationToken = payload.passwordCreationToken;
        state.passwordCreated = false;
    },
    setRefNo(state, payload) {
        state.refNo = payload.refNo;
    },
    setPasswordCreationToken(state, payload) {
        state.passwordCreationToken = payload.passwordCreationToken;
    },
    setPasswordCreated(state, payload) {
        state.passwordCreated = payload.passwordCreated;
    },
    setPassword(state, payload) {
        state.password = payload.password;
        state.errors = clearErrors(state.errors, 'password');
    },
    setPasswordConfirmation(state, payload) {
        state.passwordConfirmation = payload.passwordConfirmation;
        state.errors = clearErrors(state.errors, 'passwordConfirmation');
    },
    setPasswordStrength(state, payload) {
        state.passwordStrength = payload.passwordStrength;
    },
    setIsSaving(state, payload) {
        state.isSaving = payload.isSaving;
        if (state.isSaving) {
            NProgress.start();
        } else {
            NProgress.done();
        }
    },
    setErrors(state, payload) {
        state.errors = payload.errors;
    },
};

export const actions: ActionTree<ConfirmState, RootState> = {
    async signUp({ state, commit, rootState }) {
        const errors = await validateSchema(confirmValidationSchema, {
            password: state.password,
            passwordConfirmation: state.passwordConfirmation,
        });
        if (errors) {
            return commit('setErrors', { errors });
        }
        try {
            commit('setIsSaving', { isSaving: true });
            await axios.post(`${rootState.instanceId}/portal_sign_up`, {
                password: state.password,
                passwordCreationToken: state.passwordCreationToken,
            });
            commit('setPassword', { password: '' });
            commit('setPasswordConfirmation', { passwordConfirmation: '' });
        } finally {
            commit('setIsSaving', { isSaving: false });
        }
    },
};

const ConfirmStore: Module<ConfirmState, RootState> = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};

export default ConfirmStore;
